import React from 'react';
import { MessageCircle, Send, Paperclip, MoreHorizontal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Textarea } from '../ui/textarea';

const ClientMessages = () => {
  const conversations = [
    {
      id: 1,
      client: 'Sarah Johnson',
      company: 'Microsoft Corporation',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=50&h=50&fit=crop&crop=face',
      lastMessage: 'Thanks for the excellent workshop! The team loved it.',
      time: '2 hours ago',
      unread: false,
      status: 'active'
    },
    {
      id: 2,
      client: 'Mike Chen',
      company: 'Amazon Web Services',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face',
      lastMessage: 'Can we schedule a follow-up session for next month?',
      time: '5 hours ago',
      unread: true,
      status: 'active'
    },
    {
      id: 3,
      client: 'Lisa Wang',
      company: 'Google LLC',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face',
      lastMessage: 'We need to adjust the agenda for the innovation workshop.',
      time: '1 day ago',
      unread: true,
      status: 'urgent'
    }
  ];

  const selectedConversation = conversations[1]; // Mike Chen for demo

  const messages = [
    {
      id: 1,
      sender: 'Mike Chen',
      message: 'Hi! Hope you\'re doing well. I wanted to follow up on our recent training session.',
      time: '10:30 AM',
      isClient: true
    },
    {
      id: 2,
      sender: 'You',
      message: 'Hello Mike! Great to hear from you. I\'m glad you reached out. How did the team find the session?',
      time: '10:45 AM',
      isClient: false
    },
    {
      id: 3,
      sender: 'Mike Chen',
      message: 'The feedback has been overwhelmingly positive! The team especially loved the interactive elements and practical exercises.',
      time: '11:00 AM',
      isClient: true
    },
    {
      id: 4,
      sender: 'Mike Chen',
      message: 'Can we schedule a follow-up session for next month? We\'d like to dive deeper into some of the topics.',
      time: '11:02 AM',
      isClient: true
    }
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Client Messages</span>
            <div className="flex items-center space-x-2">
              <Badge className="bg-red-500 text-white">
                3 Unread
              </Badge>
              <Button variant="outline" size="sm">
                Mark All Read
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-5 gap-6 h-96">
        {/* Conversations List */}
        <div className="col-span-2">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Conversations</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 h-full overflow-y-auto">
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                      conv.id === selectedConversation.id
                        ? 'bg-green-50 border-2 border-green-200'
                        : 'hover:bg-gray-50 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={conv.avatar} alt={conv.client} />
                          <AvatarFallback>{conv.client.charAt(0)}</AvatarFallback>
                        </Avatar>
                        {conv.unread && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
                        )}
                        {conv.status === 'urgent' && (
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-orange-500 rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className={`font-medium text-sm truncate ${conv.unread ? 'font-semibold' : ''}`}>
                            {conv.client}
                          </h4>
                          <span className="text-xs text-gray-500">{conv.time}</span>
                        </div>
                        <p className="text-xs text-gray-600 truncate">{conv.company}</p>
                        <p className={`text-sm truncate ${conv.unread ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                          {conv.lastMessage}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chat Area */}
        <div className="col-span-3">
          <Card className="h-full flex flex-col">
            {/* Chat Header */}
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={selectedConversation.avatar} alt={selectedConversation.client} />
                    <AvatarFallback>{selectedConversation.client.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h4 className="font-semibold">{selectedConversation.client}</h4>
                    <p className="text-sm text-gray-600">{selectedConversation.company}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline">Active Project</Badge>
                  <Button variant="ghost" size="sm">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            {/* Messages Area */}
            <CardContent className="flex-1 overflow-y-auto">
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.isClient ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      msg.isClient
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-green-600 text-white'
                    }`}>
                      <p className="text-sm">{msg.message}</p>
                      <p className={`text-xs mt-1 ${
                        msg.isClient ? 'text-gray-500' : 'text-green-100'
                      }`}>
                        {msg.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>

            {/* Message Input */}
            <div className="p-4 border-t">
              <div className="flex items-end space-x-2">
                <Button variant="ghost" size="sm">
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Textarea
                  placeholder="Type your message..."
                  className="flex-1 resize-none"
                  rows={2}
                />
                <Button size="sm" className="bg-green-600 hover:bg-green-700">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientMessages;